import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import styles from './LocationManagement.module.css';
import { locationApi, Country } from '../../services/locationApi';

const LocationManagement: React.FC = () => {
  const [countries, setCountries] = useState<Country[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState<boolean>(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadCountries();
  }, []);

  const loadCountries = async () => {
    try {
      setLoading(true);
      const data = await locationApi.getAllCountries();
      
      if (!Array.isArray(data)) {
        console.error('Invalid response format:', data);
        toast.error('Invalid response format from server', {
          position: "top-right",
          autoClose: 5000
        });
        return;
      }
      
      setCountries(data);
    } catch (error: any) {
      console.error('Error fetching countries:', error);
      const errorMessage = error?.response?.data?.detail || 'Failed to load countries. Please try again later.';
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 5000
      });
    } finally {
      setLoading(false);
    }
  };

  // Filter countries based on search term
  const filteredCountries = countries.filter(country => {
    if (!searchTerm) return true;
    
    const search = searchTerm.toLowerCase();
    return (
      (country.name || '').toLowerCase().includes(search) ||
      (country.abbrev || '').toLowerCase().includes(search)
    );
  });

  const handleDeleteCountry = (countryId: string) => {
    const country = countries.find(c => c.Country_ID === countryId);
    const countryName = country ? country.name : `ID ${countryId}`;
    
    toast.warn(
      <div style={{ minWidth: '300px' }}>
        <p className="mb-3 fw-bold">Are you sure you want to delete the country "{countryName}"?</p>
        <div className="d-flex justify-content-center gap-3">
          <button 
            className="btn btn-secondary px-4" 
            onClick={() => toast.dismiss()}
          >
            Cancel
          </button>
          <button 
            className="btn btn-danger px-4" 
            onClick={async () => {
              toast.dismiss();
              try {
                await locationApi.deleteCountry(countryId);
                setCountries(prevCountries => prevCountries.filter(c => c.Country_ID !== countryId));
                toast.success('Country deleted successfully', {
                  position: "top-right",
                  autoClose: 3000
                });
              } catch (error: any) {
                console.error('Error deleting country:', error);
                const errorMessage = error?.response?.data?.detail || 'Failed to delete country. Please try again.';
                toast.error(errorMessage, {
                  position: "top-right",
                  autoClose: 3000
                });
              }
            }}
          >
            Delete
          </button>
        </div>
      </div>,
      {
        position: "top-right",
        autoClose: false,
        hideProgressBar: true,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        closeButton: false,
      }
    );
  };

  const navigateToCountry = (countryId: string) => {
    navigate(`/admin/locations/${countryId}`);
  };

  if (loading) {
    return (
      <div className={`container mt-5 ${styles.loadingContainer}`}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <div className="mt-2">Loading countries...</div>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <div className="container mt-4">
        <nav aria-label="breadcrumb">
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/admin"><i className="bi bi-house-door me-1"></i>Admin</Link>
            </li>
            <li className="breadcrumb-item active" aria-current="page">Location Management</li>
          </ol>
        </nav>

        <div className="mb-4">
          <h2 className="mb-1">Location Management</h2>
          <p className="text-muted mb-0">
            <i className="bi bi-geo-alt me-2"></i>
            Manage countries and cities
          </p>
        </div>

        <div className={styles.tableContainer}>
          <div className={styles.tableHeader}>
            <div className={styles.tableHeaderTop}>
              <div className="d-flex align-items-center">
                <i className="bi bi-table me-2 text-primary"></i>
                <h5 className="mb-0">Countries</h5>
              </div>
              <div className={styles.headerActions}>
                <span className="badge bg-primary rounded-pill">
                  {filteredCountries.length} {filteredCountries.length === 1 ? 'Country' : 'Countries'}
                </span>
                <button 
                  className="btn btn-outline-primary btn-sm"
                  onClick={loadCountries}
                  title="Refresh countries"
                >
                  <i className="bi bi-arrow-clockwise me-1"></i>
                  Refresh
                </button>
                <Link 
                  className="btn btn-primary btn-sm" 
                  to="/admin/locations/create"
                >
                  <i className="bi bi-plus-circle me-2"></i>
                  New Country
                </Link>
              </div>
            </div>
          </div>
          
          {/* Search Bar */}
          <div className="p-3 border-bottom bg-white">
            <div className="input-group">
              <span className="input-group-text bg-white">
                <i className="bi bi-search"></i>
              </span>
              <input 
                type="text"
                className="form-control"
                placeholder="Search by name or abbreviation..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button 
                  className="btn btn-outline-secondary"
                  onClick={() => setSearchTerm('')}
                  title="Clear search"
                >
                  <i className="bi bi-x-lg"></i>
                </button>
              )}
            </div>
          </div>
          
          {/* Desktop Table View */}
          <div className={styles.desktopTable}>
            <table className={`table ${styles.locationTable}`}>
              <thead>
                <tr>
                  <th>
                    <i className="bi bi-globe me-1"></i>
                    Country
                  </th>
                  <th>
                    <i className="bi bi-tag me-1"></i>
                    Abbreviation
                  </th>
                  <th>
                    <i className="bi bi-buildings me-1"></i>
                    Cities
                  </th>
                  <th>
                    <i className="bi bi-tools me-1"></i>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredCountries.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="text-center p-5">
                      <div className={styles.emptyState}>
                        {searchTerm ? (
                          <>
                            <i className="bi bi-search display-4 text-muted mb-3"></i>
                            <h5 className="text-muted">No countries found</h5>
                            <p className="text-muted mb-3">
                              Try adjusting your search criteria.
                            </p>
                            <button 
                              className="btn btn-outline-secondary"
                              onClick={() => setSearchTerm('')}
                            >
                              <i className="bi bi-x-circle me-2"></i>
                              Clear Search
                            </button>
                          </>
                        ) : (
                          <>
                            <i className="bi bi-inbox display-4 text-muted mb-3"></i>
                            <h5 className="text-muted">No countries configured</h5>
                            <p className="text-muted mb-3">
                              Create your first country to get started.
                            </p>
                            <Link 
                              className="btn btn-primary"
                              to="/admin/locations/create"
                            >
                              <i className="bi bi-plus-circle me-2"></i>
                              Create First Country
                            </Link>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredCountries.map((country, index) => (
                    <tr key={country.Country_ID} className={index % 2 === 0 ? styles.evenRow : styles.oddRow}>
                      <td className={styles.locationTableCell}>
                        <strong>{country.name}</strong>
                      </td>
                      <td className={styles.locationTableCell}>
                        <span className={styles.abbrevBadge}>{country.abbrev}</span>
                      </td>
                      <td className={styles.locationTableCell}>
                        <span className="badge bg-info text-dark">
                          {country.city_count || 0} {(country.city_count || 0) === 1 ? 'city' : 'cities'}
                        </span>
                      </td>
                      <td className={`${styles.locationTableCell} text-end`}>
                        <div className={styles.actionButtons}>
                          <button 
                            className="btn btn-sm btn-outline-primary" 
                            onClick={() => navigateToCountry(country.Country_ID)}
                            title="Edit country"
                          >
                            <i className="bi bi-pencil-fill"></i>
                          </button>
                          <button 
                            className="btn btn-sm btn-outline-danger" 
                            onClick={() => handleDeleteCountry(country.Country_ID)}
                            title="Delete country"
                          >
                            <i className="bi bi-trash-fill"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className={styles.mobileCards}>
            {filteredCountries.length === 0 ? (
              <div className="text-center p-5">
                <div className={styles.emptyState}>
                  {searchTerm ? (
                    <>
                      <i className="bi bi-search display-4 text-muted mb-3"></i>
                      <h5 className="text-muted">No countries found</h5>
                      <p className="text-muted mb-3">
                        Try adjusting your search criteria.
                      </p>
                      <button 
                        className="btn btn-outline-secondary"
                        onClick={() => setSearchTerm('')}
                      >
                        <i className="bi bi-x-circle me-2"></i>
                        Clear Search
                      </button>
                    </>
                  ) : (
                    <>
                      <i className="bi bi-inbox display-4 text-muted mb-3"></i>
                      <h5 className="text-muted">No countries configured</h5>
                      <p className="text-muted mb-3">
                        Create your first country to get started.
                      </p>
                      <Link 
                        className="btn btn-primary"
                        to="/admin/locations/create"
                      >
                        <i className="bi bi-plus-circle me-2"></i>
                        Create First Country
                      </Link>
                    </>
                  )}
                </div>
              </div>
            ) : (
              filteredCountries.map((country) => (
                <div key={country.Country_ID} className={styles.locationCard}>
                  <div className={styles.locationCardHeader}>
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <h6 className="mb-0 fw-bold">{country.name}</h6>
                      <span className={styles.abbrevBadge}>{country.abbrev}</span>
                    </div>
                  </div>
                  
                  <div className={styles.locationCardBody}>
                    <div className="mb-2">
                      <small className="text-muted">
                        <i className="bi bi-buildings me-1"></i>
                        Cities:
                      </small>
                      <div className="mt-1">
                        <span className="badge bg-info text-dark">
                          {country.city_count || 0} {(country.city_count || 0) === 1 ? 'city' : 'cities'}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className={styles.locationCardActions}>
                    <button 
                      className="btn btn-sm btn-outline-primary flex-fill"
                      onClick={() => navigateToCountry(country.Country_ID)}
                      title="Edit Country"
                    >
                      <i className="bi bi-pencil-fill me-1"></i>
                      Edit
                    </button>
                    <button 
                      className="btn btn-sm btn-outline-danger flex-fill" 
                      onClick={() => handleDeleteCountry(country.Country_ID)}
                      title="Delete Country"
                    >
                      <i className="bi bi-trash-fill me-1"></i>
                      Delete
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LocationManagement;
